"use client";
import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const services = [
{
  num: '01',
  title: 'Temporary Staffing',
  desc: 'Rapid deployment of pre-screened, QIYAS-compliant workers for short-term project peaks — deployed within 72 hours of confirmation.',
  image: "https://images.unsplash.com/photo-1577199001468-44c049e7603f",
  alt: 'Construction workers in hard hats on a job site'
},
{
  num: '02',
  title: 'Contract Staffing',
  desc: 'Long and short-term workforce on Fornax Global visa — we handle all employment obligations, leaving you focused on project delivery.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1940feb94-1769465193898.png",
  alt: 'Business professionals signing a contract in an office'
},
{
  num: '03',
  title: 'Blue Collar Staffing',
  desc: 'Certified skilled tradespeople, heavy equipment operators, and technicians — tested, trained, and ready for demanding site conditions.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18c0d8cc7-1772906005646.png",
  alt: 'Skilled worker operating heavy machinery on a construction site'
},
{
  num: '04',
  title: 'Overseas Recruitment',
  desc: 'Full international hiring lifecycle — from overseas sourcing and skills verification to medical, visa processing, and Qatar arrival.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1402dee12-1772906006470.png",
  alt: 'Airplane flying over clouds representing international recruitment'
},
{
  num: '05',
  title: 'Local & Offshore Recruitment',
  desc: 'End-to-end cross-border talent acquisition for both locally available candidates and offshore professionals across all disciplines.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18e307919-1772798180213.png",
  alt: 'HR professionals conducting recruitment interviews in a modern office'
},
{
  num: '06',
  title: 'HR Management & Consulting',
  desc: 'Dedicated account managers and strategic HR advisors who embed into your operations to optimize workforce performance and compliance.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_117a60fbb-1771901657225.png",
  alt: 'HR consultant presenting workforce strategy to executive team'
}];


export default function ServicesGrid() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-hidden').forEach((el) => {
              el.classList.add('revealed');
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="services" ref={ref} className="relative py-24 overflow-hidden" style={{ background: '#071e3d' }}>
      <div className="glow-orb w-[600px] h-[600px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-20"
      style={{ background: 'rgba(0,194,255,0.2)' }} />
      
      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16 reveal-hidden">
          <p className="eyebrow mb-4">WHAT WE OFFER</p>
          <h2
            className="text-3xl lg:text-5xl font-extrabold text-fg"
            style={{ fontFamily: 'Syne, sans-serif' }}>
            
            Comprehensive{' '}
            <span className="gradient-text">Workforce Solutions</span>
          </h2>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services?.map((s, i) =>
          <div
            key={s?.num}
            className={`reveal-hidden stagger-${Math.min(i + 1, 6)} card-hover rounded-2xl overflow-hidden relative group`}
            style={{
              background: '#0a2450',
              border: '1px solid rgba(255,255,255,0.06)'
            }}>
            
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <AppImage
                src={s?.image}
                alt={s?.alt}
                fill
                className="object-cover transition-transform duration-500 group-hover:scale-105"
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw" />
              
                {/* Gradient overlay */}
                <div
                className="absolute inset-0"
                style={{ background: 'linear-gradient(to bottom, rgba(2,13,31,0.2) 0%, rgba(10,36,80,0.85) 100%)' }} />
              
                {/* Card Number */}
                <span className="card-number">{s?.num}</span>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3
                className="text-fg font-bold text-lg mb-3"
                style={{ fontFamily: 'Syne, sans-serif' }}>
                
                  {s?.title}
                </h3>
                <p
                className="text-muted text-sm leading-relaxed mb-5"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 300 }}>
                
                  {s?.desc}
                </p>
                <button
                onClick={() => {
                  const el = document.getElementById('contact');
                  if (el) el?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="text-cyan text-sm font-semibold flex items-center gap-1 transition-all duration-300 hover:gap-2 group/btn"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                
                  Learn More
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="transition-transform duration-300 group-hover/btn:translate-x-1">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </button>
              </div>

              {/* Cyan border glow on hover */}
              <div
              className="absolute inset-0 rounded-2xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{ boxShadow: 'inset 0 0 0 1px rgba(0,194,255,0.35)' }} />
            
            </div>
          )}
        </div>
      </div>
    </section>);

}